import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { SEOHead } from '../components/SEOHead';
import { PAGE_SEO, SEOMetadata, DEFAULT_SEO } from '../config/seoConfig';

interface UseSEOOptions extends Partial<SEOMetadata> {
  pageName?: keyof typeof PAGE_SEO;
}

/**
 * Hook personnalisé pour gérer facilement le SEO par page
 * 
 * Utilisation:
 * 
 * useSEO({
 *   title: 'Ma Page',
 *   description: 'Description de ma page',
 *   image: 'https://example.com/image.jpg'
 * });
 * 
 * Ou avec un nom de page pré-configuré:
 * 
 * useSEO({ pageName: 'home' });
 */
export const useSEO = (options: UseSEOOptions = {}) => {
  const location = useLocation();

  useEffect(() => {
    // Construire les métadonnées
    let seoData: Partial<SEOMetadata> = { ...DEFAULT_SEO };

    // Si un nom de page est fourni, utiliser la config pré-définie
    if (options.pageName && PAGE_SEO[options.pageName]) {
      seoData = { ...seoData, ...PAGE_SEO[options.pageName] };
    }

    // Fusionner avec les options personnalisées
    seoData = { ...seoData, ...options };

    // Ajouter l'URL actuelle si pas de URL personnalisée
    if (!seoData.url) {
      seoData.url = `https://tournoispadel.fr${location.pathname}`;
    }

    // Créer et monter le composant SEOHead
    const seoHeadContainer = document.getElementById('seo-head');
    if (seoHeadContainer) {
      // Ce sera géré par le composant SEOHead dans App.tsx
    }

    // Log pour debug
    console.log('🔍 useSEO hook - Applied SEO:', {
      title: seoData.title,
      description: seoData.description?.substring(0, 50) + '...',
      pathname: location.pathname,
    });
  }, [location.pathname, options]);

  return null;
};

/**
 * Hook pour mettre à jour le SEO basé sur la route actuelle
 * Utilisé par le composant SEOHead dans App.tsx
 */
export const useUpdateSEO = (metadata: Partial<SEOMetadata> = {}) => {
  const location = useLocation();

  useEffect(() => {
    let seoData: Partial<SEOMetadata> = { ...DEFAULT_SEO, ...metadata };

    // Ajouter l'URL actuelle
    if (!seoData.url) {
      seoData.url = `https://tournoispadel.fr${location.pathname}`;
    }

    // Créer les meta tags
    const updateMeta = (name: string, content: string, isProperty = false) => {
      let element = document.querySelector(
        isProperty ? `meta[property="${name}"]` : `meta[name="${name}"]`
      );
      if (!element) {
        element = document.createElement('meta');
        if (isProperty) {
          element.setAttribute('property', name);
        } else {
          element.setAttribute('name', name);
        }
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    // Mettre à jour le titre
    document.title = seoData.title || DEFAULT_SEO.title;

    // Métadonnées basiques
    updateMeta('description', seoData.description || DEFAULT_SEO.description);

    // Open Graph
    updateMeta('og:title', seoData.title || DEFAULT_SEO.title, true);
    updateMeta('og:description', seoData.description || DEFAULT_SEO.description, true);
    updateMeta('og:image', (seoData.image || DEFAULT_SEO.image) as string, true);
    updateMeta('og:url', (seoData.url || DEFAULT_SEO.url) as string, true);

    console.log('🔍 SEO Updated:', seoData.title);
  }, [location.pathname, metadata]);
};
